import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  X,
  Loader2,
  Home,
  ShieldCheck,
  Network,
  FileText,
  Files,
  PanelRightClose,
  CheckCircle2,
} from 'lucide-react';

import { useArtifactsPanel } from '../../context/ArtifactsPanelContext';
import type { PanelArtifact } from '../../context/ArtifactsPanelContext';
import { useFileTreeContext } from '../../context/FileTreeContext';
import type { FileNode } from '../../types';
import apiService from '../../services/api';

// Lazy-load each artifact body. Splits the bundle so users who never open
// an artifact (most sessions) don't pay for D3, recharts, etc.
const ValuationArtifact = lazy(() => import('../Artifacts/ValuationArtifact'));
const UnderwritingArtifact = lazy(() => import('../Artifacts/UnderwritingArtifact'));
const GraphArtifact = lazy(() => import('../Artifacts/GraphArtifact'));
const ReportArtifact = lazy(() => import('../Artifacts/ReportArtifact'));

const artifactMeta = (a: PanelArtifact): { title: string; icon: React.ReactNode } => {
  switch (a.kind) {
    case 'valuation':
      return { title: 'שמאות', icon: <Home className="h-4 w-4" /> };
    case 'underwriting':
      return { title: 'חיתום', icon: <ShieldCheck className="h-4 w-4" /> };
    case 'knowledge_graph':
      return { title: 'גרף ידע', icon: <Network className="h-4 w-4" /> };
    case 'report':
      return { title: 'דוח', icon: <FileText className="h-4 w-4" /> };
  }
};

const renderArtifact = (a: PanelArtifact): React.ReactNode => {
  switch (a.kind) {
    case 'valuation':
      return <ValuationArtifact projectId={a.projectId} valuationId={a.valuationId} />;
    case 'underwriting':
      return <UnderwritingArtifact projectId={a.projectId} contextId={a.contextId} />;
    case 'knowledge_graph':
      return <GraphArtifact projectId={a.projectId} />;
    case 'report':
      return <ReportArtifact title={a.title} markdown={a.markdown} />;
  }
};

// ── Sources view ──────────────────────────────────────────────────────────

interface ProjectDocLite {
  id: number;
  filename: string;
  status?: string;
  page_count?: number | null;
}

interface ProjectDetailLite {
  id: string;
  name: string;
  documents?: ProjectDocLite[];
}

const STATUS_LABELS: Record<string, string> = {
  uploaded: 'הועלה',
  processing: 'מעבד',
  ocr_processing: 'מעבד OCR',
  processed: 'מוכן',
  completed: 'מוכן',
  ready: 'מוכן',
  failed: 'נכשל',
};

/** Flatten the file tree into an id → node map so we can resolve the
    filename/status of an explicitly-selected `doc-<id>` source. */
const flattenTree = (nodes: FileNode[], out = new Map<string, FileNode>()): Map<string, FileNode> => {
  for (const node of nodes) {
    out.set(node.id, node);
    if (node.children && node.children.length > 0) flattenTree(node.children, out);
  }
  return out;
};

/** All library documents (file nodes) excluding the recycle bin — the source
    catalog offered when no project scopes the chat. Lets the user compose a
    NotebookLM-style multi-document context from previously uploaded files. */
const collectLibraryFiles = (nodes: FileNode[], out: FileNode[] = []): FileNode[] => {
  for (const node of nodes) {
    if (node.id === 'recycle-bin') continue;
    if (node.type === 'file' && node.document) out.push(node);
    if (node.children && node.children.length > 0) collectLibraryFiles(node.children, out);
  }
  return out;
};

/** Parse `/p/:projectId/...` out of the current path (the context panel lives
    in the shell, outside the routed Outlet, so it can't use useParams). */
const useActiveProjectId = (): string | null => {
  const location = useLocation();
  return useMemo(() => {
    const m = location.pathname.match(/\/p\/([^/]+)/);
    const id = m?.[1];
    // `/p` (projects index) has no id segment we care about.
    return id && id !== 'new' ? id : null;
  }, [location.pathname]);
};

interface SourceRow {
  key: string; // selection key, e.g. "doc-20"
  name: string;
  status?: string;
  pageCount?: number | null;
  explicit: boolean;   // explicitly scoped into the chat (session source / picked) → highlight + remove
  removable: boolean;  // came from a session source → click removes it; otherwise toggles selection
}

const SourcesView: React.FC = () => {
  const {
    tree,
    selectedFileIds,
    sessionSourceIds,
    toggleFileSelection,
    addSessionSource,
    removeSessionSource,
    trackedUploads,
  } = useFileTreeContext();
  const projectId = useActiveProjectId();
  const [project, setProject] = useState<ProjectDetailLite | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setProject(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiService
      .getProject(projectId)
      .then((p) => {
        if (!cancelled) setProject(p as ProjectDetailLite);
      })
      .catch(() => {
        if (!cancelled) setProject(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const nodeMap = useMemo(() => flattenTree(tree), [tree]);

  // Filename/status fallback for docs uploaded this session that the tree
  // hasn't returned yet — otherwise a fresh source briefly shows its raw id.
  const uploadInfo = useMemo(() => {
    const m = new Map<string, { name: string; status?: string }>();
    for (const u of trackedUploads) {
      m.set(`doc-${u.documentId}`, { name: u.filename, status: u.status });
    }
    return m;
  }, [trackedUploads]);

  // The candidate list, deduped by *filename*: project documents (when a project
  // is active) OR the whole personal library (no project — so the user can pick
  // several previously-uploaded documents and ask across all of them), PLUS the
  // chat's persistent sources and any ad-hoc tree selections.
  // Deduping by name (not just id) collapses the case where the same file exists
  // under two ids — e.g. a project doc plus an in-chat upload, or a double
  // upload — which previously showed the file twice. The richest metadata
  // (page count) and "in scope" status are merged onto the single row.
  const rows: SourceRow[] = useMemo(() => {
    const byName = new Map<string, SourceRow>();
    const upsert = (
      key: string,
      name: string,
      status: string | undefined,
      pageCount: number | null | undefined,
      explicit: boolean,
      removable: boolean,
    ) => {
      const existing = byName.get(name);
      if (!existing) {
        byName.set(name, { key, name, status, pageCount, explicit, removable });
        return;
      }
      existing.explicit = existing.explicit || explicit;
      // Prefer a removable (session-source) key so the row's remove action works.
      if (removable && !existing.removable) {
        existing.removable = true;
        existing.key = key;
      }
      if (existing.pageCount == null && pageCount != null) existing.pageCount = pageCount;
      if (!existing.status && status) existing.status = status;
    };

    if (projectId && project?.documents) {
      for (const d of project.documents) {
        upsert(`doc-${d.id}`, d.filename, d.status, d.page_count, false, false);
      }
    } else if (!projectId) {
      const library = collectLibraryFiles(tree).sort((a, b) =>
        (b.document?.created_at || '').localeCompare(a.document?.created_at || ''),
      );
      for (const node of library) {
        upsert(node.id, node.name, node.document?.status, node.document?.page_count, false, false);
      }
    }
    for (const id of Array.from(sessionSourceIds)) {
      const node = nodeMap.get(id);
      const up = uploadInfo.get(id);
      upsert(id, node?.name || up?.name || id, node?.document?.status || up?.status, node?.document?.page_count, true, true);
    }
    for (const id of Array.from(selectedFileIds)) {
      const node = nodeMap.get(id);
      const up = uploadInfo.get(id);
      upsert(id, node?.name || up?.name || id, node?.document?.status || up?.status, node?.document?.page_count, true, false);
    }
    return Array.from(byName.values());
  }, [projectId, project, tree, sessionSourceIds, selectedFileIds, nodeMap, uploadInfo]);

  // Items explicitly scoped into the chat (sources + ad-hoc picks).
  const scopedCount = sessionSourceIds.size + selectedFileIds.size;

  // A quick name filter — the personal library can grow long; the in-scope
  // documents always stay visible at the top regardless of the filter.
  const [filter, setFilter] = useState('');
  const visibleRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matched = q
      ? rows.filter((r) => r.explicit || r.name.toLowerCase().includes(q))
      : rows;
    // In-scope documents first so the chat's active context is always in view.
    return [...matched].sort((a, b) => Number(b.explicit) - Number(a.explicit));
  }, [rows, filter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="px-4 py-10 text-center">
        <Files className="h-9 w-9 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500 leading-relaxed">
          אין מסמכים בהקשר.
          <br />
          בחר פרויקט או צרף קובץ בצ׳אט כדי שאתבסס עליו.
        </p>
      </div>
    );
  }

  return (
    <div className="px-2 py-2">
      {projectId && project?.name ? (
        <div className="px-2 pb-2 text-xs text-gray-500">
          פרויקט: <span className="font-medium text-gray-700">{project.name}</span>
          {scopedCount > 0 && (
            <span className="text-gray-400"> · {scopedCount} נבחרו להקשר</span>
          )}
        </div>
      ) : (
        <div className="px-2 pb-2 text-xs text-gray-500 leading-relaxed">
          {scopedCount > 0 ? (
            <><span className="font-medium text-gray-700">{scopedCount}</span> מסמכים בהקשר השיחה — אפשר לשאול על כולם יחד.</>
          ) : (
            <>סמן מסמכים מהספרייה כדי לשאול עליהם יחד (אפשר כמה במקביל).</>
          )}
        </div>
      )}
      {rows.length > 8 && (
        <div className="px-2 pb-2">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="סינון לפי שם קובץ…"
            dir="rtl"
            className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-500/20"
            aria-label="סינון מסמכים לפי שם"
          />
        </div>
      )}
      <ul className="space-y-0.5">
        {visibleRows.map((row) => {
          // When a project is active and the user has NOT narrowed to specific
          // docs or attached chat sources, every project doc is implicitly in scope.
          const implicitlyInScope =
            Boolean(projectId) && selectedFileIds.size === 0 && sessionSourceIds.size === 0;
          const inScope = row.explicit || implicitlyInScope;
          return (
            <li key={row.key}>
              <button
                type="button"
                onClick={() => {
                  // Adding from the panel attaches the doc as a *persistent*
                  // chat source (NotebookLM-style — survives across turns),
                  // not a one-shot pick that vanishes after the first send.
                  if (!row.explicit) addSessionSource(row.key);
                  else if (row.removable) removeSessionSource(row.key);
                  else toggleFileSelection(row.key);
                }}
                title={row.explicit ? 'הסר מההקשר' : 'הוסף להקשר'}
                className={`group w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-right transition-colors ${
                  row.explicit
                    ? 'bg-primary-50 ring-1 ring-primary-200'
                    : 'hover:bg-gray-50'
                }`}
              >
                <span
                  className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${
                    inScope ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-gray-800">{row.name}</span>
                  <span className="block text-[11px] text-gray-400">
                    {STATUS_LABELS[row.status || ''] || row.status || ''}
                    {row.pageCount ? ` · ${row.pageCount} עמ׳` : ''}
                  </span>
                </span>
                {row.explicit && (
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-primary-600" />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ── Panel header + body switch ─────────────────────────────────────────────

const PanelInner: React.FC<{ onCollapse: () => void }> = ({ onCollapse }) => {
  const { artifact, isOpen: artifactOpen, closeArtifact } = useArtifactsPanel();

  if (artifactOpen && artifact) {
    const meta = artifactMeta(artifact);
    return (
      <>
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2 text-slate-800">
            <span className="text-primary-600">{meta.icon}</span>
            <h2 className="text-sm font-semibold">{meta.title}</h2>
          </div>
          <button
            onClick={closeArtifact}
            className="p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100 press-sm"
            title="חזרה למקורות"
            aria-label="סגור artifact"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-app animate-fade-in motion-reduce:animate-none">
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            }
          >
            {renderArtifact(artifact)}
          </Suspense>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-2 text-slate-800">
          <Files className="h-4 w-4 text-primary-600" />
          <h2 className="text-sm font-semibold">מקורות</h2>
        </div>
        <button
          onClick={onCollapse}
          className="p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          title="הסתר פאנל (⌘.)"
          aria-label="הסתר פאנל מקורות"
        >
          <PanelRightClose className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-auto bg-app">
        <SourcesView />
      </div>
    </>
  );
};

/**
 * The leading-edge (left in RTL) third column.
 *
 *  - Default content: **Sources** — the documents currently in the chat's
 *    context (project docs or explicitly selected files). Clicking a source
 *    toggles whether it's scoped into the next message.
 *  - When the user asks for valuation / underwriting / knowledge-graph, the
 *    same column swaps to render that **artifact** (and a close button returns
 *    to sources).
 *
 *  - **Desktop (≥md)** — a flex sibling column. Width adapts: narrow for
 *    sources, wider for artifacts.
 *  - **Mobile (<md)** — artifacts slide up as a bottom sheet; the sources list
 *    is desktop-only (mobile users browse files via the sidebar).
 */
const ContextPanel: React.FC<{ isOpen: boolean; onCollapse: () => void }> = ({
  isOpen,
  onCollapse,
}) => {
  const { artifact, isOpen: artifactOpen, closeArtifact } = useArtifactsPanel();

  return (
    <>
      {/* Desktop column */}
      <div
        className={`hidden md:flex md:flex-col bg-app border-r border-gray-200 transition-[width,box-shadow] duration-300 ease-drawer overflow-hidden flex-shrink-0 ${
          isOpen
            ? artifactOpen
              ? 'w-[440px] lg:w-[520px] xl:w-[600px] shadow-xl'
              : 'w-[300px] lg:w-[340px]'
            : 'w-0 border-r-0 shadow-none'
        }`}
        aria-hidden={!isOpen}
        dir="rtl"
      >
        {isOpen && <PanelInner onCollapse={onCollapse} />}
      </div>

      {/* Mobile bottom sheet — artifacts only */}
      <div
        className={`md:hidden fixed inset-0 z-50 ${artifactOpen ? '' : 'pointer-events-none'}`}
        dir="rtl"
        aria-hidden={!artifactOpen}
      >
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${
            artifactOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeArtifact}
        />
        <div
          className={`absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col transition-transform duration-300 ease-drawer ${
            artifactOpen ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          {artifact && (
            <>
              <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
                <span className="w-10 h-1 rounded-full bg-slate-300" aria-hidden />
              </div>
              <PanelInner onCollapse={onCollapse} />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ContextPanel;
